import React, { useEffect, useState } from 'react';
import uuid from 'react-uuid';
import t from 'react-translate';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import NumberInput from 'athena/components/number-input';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { useFormContext } from 'react-hook-form';
import NvPopover from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import { config } from '@config/pendo.config.json';
import { overlayStyles } from '../../program-setup/styles';

const MIN_MENTEES = 1;
// TODO: Change if necessary
const MAX_MENTEES = Infinity;

type MaxMenteesSettingProps = {
  name: string;
  onSubmit: () => void;
};

const MaxMenteesSetting = React.memo((props: MaxMenteesSettingProps) => {
  const { name, onSubmit } = props;
  const id = React.useMemo(() => `target-${uuid()}`, []);
  const ref = React.useRef<HTMLDivElement>(null);
  const { mentoringProgram } = React.useContext(MentoringProgramContext);
  const [targetElementRef, setTargetElementRef] = React.useState<HTMLSpanElement | null>(null);
  const mentoringProgramAliases = useSelector((state) => getMentoringProgramAliases(state, mentoringProgram.id));
  const translationBase =
    t.MENTORING_PROGRAMS.SETTINGS.TABS.MATCHING_SETTINGS.MATCHING_TYPE
      .CONFIGURATION.ASSIGNED.CONNECTIONS;
  const { setValue, getValues } = useFormContext();
  const { maxMentees, mentorCapacityEnabled } = getValues();
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    const findTarget = () => {
      if (ref.current) {
        const foundTarget = ref.current.querySelector<HTMLSpanElement>(
          `#${id}`,
        );
        if (foundTarget) {
          setTargetElementRef(foundTarget);
        }
      }
    };

    findTarget();

    // Observe for DOM changes
    const observer = new MutationObserver(findTarget);
    if (ref.current) {
      observer.observe(ref.current, { childList: true, subtree: true });
    }

    // Cleanup observer on unmount
    return () => observer.disconnect();
  }, [id]);

  const handleConfirm = (newValue: number) => {
    setValue(name, newValue);
    onSubmit();
    setShowConfirmation(false);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleChange = (newValue: number) => {
    if (newValue === MIN_MENTEES && mentorCapacityEnabled) {
      setShowConfirmation(true);
    } else {
      handleConfirm(newValue);
    }
  };

  const ariaLabel = translationBase.ARIA_LABEL();
  const dataQATag = config.pendo.athena.mentorshipProgram.settings.programSetup
    .concurrentMentorConnectionInputField;

  return (
    <div ref={ref}>
      {translationBase.DESCRIPTION({
        inputId: id,
        ...mentoringProgramAliases,
      })}
      {targetElementRef
        && ReactDOM.createPortal(
          <div className='d-inline-block'>
            <NvPopover
              enabled
              show={showConfirmation}
              overlayStyles={overlayStyles}
              content={(
                <NvConfirmationPopover
                  onCancel={handleCancel}
                  onConfirm={() => handleConfirm(MIN_MENTEES)}
                  header={translationBase.WARNING.TITLE()}
                  message={translationBase.WARNING.CONTENT(
                    mentoringProgramAliases,
                  )}
                  cancelBtnText={t.FORM.CANCEL()}
                  confirmBtnText={t.FORM.OK()}
                />
              )}
              placement='top'
              rootClose
              onHide={() => setShowConfirmation(false)}
              className='d-inline-flex mw-100'
              preventOverflow
            >
              <NumberInput
                name={name}
                min={MIN_MENTEES}
                max={MAX_MENTEES}
                aria-label={ariaLabel}
                value={maxMentees}
                onChange={handleChange}
                data-qa={dataQATag}
              />
            </NvPopover>
          </div>,
          targetElementRef,
        )}
    </div>
  );
});

export default MaxMenteesSetting;
